import { ref, Ref } from "vue";
import * as Sentry from "@sentry/vue";
import UserTransactionDataService from "@/services/UserTransactionDataService";
import SystemDataService from "@/services/SystemDataService";
import { Item } from "@models/Item";
import { TransactionCharge } from "@models/TransactionCharge";
import { useRouter } from "vue-router";
import { PaymentIntentRequest } from "@models/PaymentIntentRequest";
import Constants from "@/models/Constants";
import { AdminPaymentIntentRequest } from "@models/AdminPaymentIntentRequest";

export default function useCheckout(
  paymentIntentType: string,
  pokerRunId: string,
  refpayment: Ref<any>,
  getLineItems: () => Item[],
  getNextStep: () => number,
  fundraiser: string
): {
  stepDetails: number;
  stepSummary: number;
  stepPayment: number;
  stepAssignExtraCards: number;
  transactionId: Ref<string>;
  currentStep: Ref<number>;
  isBusy: Ref<boolean>;
  paymentIntentId: Ref<string>;
  clientSecret: Ref<string>;
  transactionCharge: Ref<TransactionCharge>;
  message: Ref<string>;
  messageDetails: Ref<string>;
  messageType: Ref<string>;
  defaultConnectedAccount: Ref<string>;
  continueCheckout: (event: any) => Promise<void>;
  cancelCheckout: () => void;
  calcServiceFee: () => Promise<void>;
  paymentSuccess: () => void;
  paymentError: (errorMessage: string) => void;
  paymentInitalized: () => void;
  autoPurchase: () => Promise<string>;
  adminAutoPurchase: (transferToName: string,  transferToPhone:string, userId?: string) => Promise<string>;
} {
  const router = useRouter();
  const stepDetails = 0;
  const stepSummary = 1;
  const stepPayment = 2;
  const stepAssignExtraCards = 3;
  const clientSecret = ref("");
  const transactionId = ref("");
  const currentStep = ref(0);
  const isBusy = ref(false);
  const paymentIntentId = ref("");
  const message = ref("");
  const messageDetails = ref("");
  const messageType = ref("success");
  const defaultConnectedAccount = ref();
  UserTransactionDataService.warmup();
  SystemDataService.getActive().then((system) => {
    if (system?.defaultConnectedAccount) {
      defaultConnectedAccount.value = system.defaultConnectedAccount;
    }
  });
  
  const transactionCharge = ref<TransactionCharge>({
    amount: 0,
    serviceFee: 0,
    subtotal: 0,
    isServiceFeeIncluded: false,
  } as TransactionCharge);

  const autoPurchase = async () => {
    transactionId.value = "";
    await calcServiceFee();
    await setupPaymentIntent();
    await UserTransactionDataService.completeTransactionDirect(transactionId.value);
    return transactionId.value;
  };

  const adminAutoPurchase = async (transferToName: string, transferToPhone: string, userId?: string) => {
    transactionId.value = "";
    const paymentIntentRequest = {
      userId: userId,
      transferToName: transferToName,
      transferToPhone: transferToPhone,
      eventId: pokerRunId,
      paymentIntentType,
      transactionDetails: {
        lineItems: getLineItems(),
        amount: transactionCharge.value?.subtotal ?? 0,
        paymentProvider: "Stripe",
        fundraiser: fundraiser,
      },
    } as AdminPaymentIntentRequest;
    const response = await UserTransactionDataService.adminCompleteTransactionDirect(paymentIntentRequest);
    transactionId.value = response.transactionId;
    return transactionId.value;
  };

  const setupPaymentIntent = async (isDirect = false) => {
    // Setup payment intent
    const paymentIntentRequest = {
      eventId: pokerRunId,
      paymentIntentType,
      transactionDetails: {
        lineItems: getLineItems(),
        amount: transactionCharge.value?.subtotal ?? 0,
        paymentProvider: "Stripe",
        fundraiser: fundraiser,
      },
    } as PaymentIntentRequest;

    if (transactionId.value) {
      paymentIntentRequest.transactionDetails.transactionId = transactionId.value;
    }

    if (paymentIntentId.value) {
      paymentIntentRequest.transactionDetails.paymentIntentId = paymentIntentId.value;
    }

    try {
      const response = isDirect ? await UserTransactionDataService.startAdminTransactionDirect(paymentIntentRequest) :
       await UserTransactionDataService.paymentIntent(paymentIntentRequest);
      clientSecret.value = response.paymentIntent?.client_secret ?? "";
      paymentIntentId.value = response.paymentIntent?.id ?? "";
      transactionId.value = response.transactionId;
    } catch (error: any) {
      Sentry.captureException(error);
      messageType.value = "danger";
      message.value = "Error initializing payments. Please try again.";
      messageDetails.value = error.message;

      throw error;
    }
  };

  const paymentSuccess = () => {
    // confirm payment with stripe
    messageType.value = "success";
    message.value = "Payment accepted";
    setTimeout(() => {
      router.push({
        name: "PaymentConfirmation",
        params: { id: pokerRunId, transactionId: transactionId.value },
      });
    }, 500);
    setTimeout(() => {
      isBusy.value = false;
    }, 2000);
  };

  const paymentError = (errorMessage: string) => {
    // confirm payment with stripe
    messageType.value = "danger";
    message.value = "Error submitting payment. Please try again.";
    messageDetails.value = errorMessage;
    isBusy.value = false;
  };

  const paymentInitalized = () => {
    isBusy.value = false;
  };

  const calcServiceFee = async (isDirect = false) => {
    try {
      const lineItems = getLineItems();
      const lineItemAmount: number = lineItems
        .map((x) => x.amount)
        .reduce((prev, curr) => prev + curr, 0);
      if (lineItemAmount > 0 && !isDirect) {
        transactionCharge.value = await UserTransactionDataService.calcServiceFee(
          pokerRunId,
          lineItemAmount as number,
          paymentIntentType === "registration"
            ? lineItems.filter((x) => x.itemType === Constants.FEE_TYPE).length
            : 0
        );
      } else {
        transactionCharge.value.amount = 0;
      }
      message.value = "";
    } catch (error: any) {
      Sentry.captureException(error);
      messageType.value = "danger";
      message.value =
        "Error initializing payments. Please go back to the event page and try to register again.";
      messageDetails.value = error.message;
    }
  };

  const completeTransactionDirect = async () => {
    try {
      await UserTransactionDataService.completeTransactionDirect(transactionId.value);
      router.push({
        name: "PaymentConfirmation",
        params: { id: pokerRunId, transactionId: transactionId.value },
      });
    } catch (error: any) {
      Sentry.captureException(error);
      messageType.value = "danger";
      message.value = "Error completing transaction. Please try again.";
      messageDetails.value = error.message;
    }
  };

  const continueCheckout = async (event: { preventDefault: () => void }) => {
    message.value = "";
    if (event) {
      event.preventDefault();
    }

    isBusy.value = true;

    if (currentStep.value === stepDetails) {
      await calcServiceFee();
      await setupPaymentIntent();
      setTimeout(() => {
        currentStep.value = getNextStep();
        isBusy.value = false;
      }, 2000);
      return;
    }

    if (currentStep.value === stepAssignExtraCards) {
      await setupPaymentIntent();
      setTimeout(() => {
        currentStep.value = getNextStep();
        isBusy.value = false;
      }, 2000);
      return;
    }

    if (currentStep.value === stepSummary) {
      if (transactionCharge.value?.subtotal === 0) {
        await completeTransactionDirect();
      }

      setTimeout(() => {
        currentStep.value = getNextStep();
        isBusy.value = false;
      }, 4000);
      return;
    }

    if (currentStep.value === stepPayment && refpayment.value) {
      if (refpayment.value._?.exposed) {
        refpayment.value._.exposed.confirmPayment();
      } else {
        refpayment.value.confirmPayment();
      }
    }
  };

  const cancelCheckout = () => {
    if (currentStep.value === stepDetails) {
      router.back();
    } else {
      currentStep.value = stepDetails;
    }
  };

  return {
    stepDetails,
    stepSummary,
    stepPayment,
    stepAssignExtraCards,
    transactionId,
    currentStep,
    isBusy,
    paymentIntentId,
    clientSecret,
    transactionCharge,
    message,
    messageDetails,
    messageType,
    defaultConnectedAccount,
    continueCheckout,
    cancelCheckout,
    calcServiceFee,
    paymentSuccess,
    paymentError,
    paymentInitalized,
    autoPurchase,
    adminAutoPurchase,
  };
}
